import { create } from 'zustand';
import {
    ComposerAreaNode,
    ComposerAttributeNode,
    ComposerAttributeOptionNode,
    ComposerEcommerceSettings,
    ComposerOptionActionNode,
    ComposerTemplateNode,
    ComposerTemplateSideRestrictions
} from '../../configurator-components/composer-editors/attributes/composerAttributesModels';
import { composerQueriesFontQuery$data } from './__generated__/composerQueriesFontQuery.graphql';
import { createSecureClonedName } from './helpers/composerHelpers';
import { ComposerTemplateNodeAdapter } from './helpers/composerNodesAdapter';
import { LazyValidateAttributes, LazyValidateOptions } from './helpers/composerValidation';

// attributes > options > actions
type ComposerStore = {
    model: MPlaza.Model | null;
    setModel: (model: MPlaza.Model) => void;

    //Scene specific
    sceneMaterialList: BABYLON.Material[];
    sceneObjectList: BABYLON.AbstractMesh[];
    sceneCameraList: BABYLON.Camera[];
    setSceneMaterialList: (materials: BABYLON.Material[]) => void;
    setSceneObjectList: (objects: BABYLON.AbstractMesh[]) => void;
    setSceneCameraList: (cameras: BABYLON.Camera[]) => void;

    modelAttributes: MPlaza.Attributes | null;

    selectedAttributes: ComposerAttributeNode[] | null;
    selectedOptions: ComposerAttributeOptionNode[] | null;
    selectedActions: ComposerOptionActionNode[] | null;

    attributes: ComposerAttributeNode[];
    attributesSteps: ComposerAttributeNode[] | null;
    attributesGroups: ComposerAttributeNode[] | null;

    templates: ComposerTemplateNode[];
    selectedTemplates: ComposerTemplateNode[] | null;
    areas: ComposerAreaNode[];
    selectedAreas: ComposerAreaNode[] | null;
    designs: Map<string, MPlaza.Design>; //key: templateID, areaID
    getDesign: (templateID: number, areaID: number) => MPlaza.Design | null;
    setDesigns: (designs: Map<string, MPlaza.Design>) => void;
    updateDesign: (templateID: number, areaID: number, design: MPlaza.Design) => void;

    //quantity settings page
    productQuantity: MPlaza.QuantityRule | null;

    ecommerceSettings: ComposerEcommerceSettings | null;

    fonts: composerQueriesFontQuery$data | null;
    setFonts: (fonts: composerQueriesFontQuery$data) => void;

    intializeAttributesInfo: (modelAttributes: MPlaza.Attributes, attributes: ComposerAttributeNode[]) => void;
    updateAttribute: (attribute: ComposerAttributeNode) => void;
    cloneAttributes: (attributes: ComposerAttributeNode[]) => ComposerAttributeNode[];
    removeAttributes: (attributeIds: string[]) => void;
    updateOption: (option: ComposerAttributeOptionNode) => void;
    cloneOption: (option: ComposerAttributeOptionNode) => ComposerAttributeOptionNode;
    removeOption: (attributeID: string, optionID: string) => void;
    updateAction: (action: ComposerOptionActionNode) => void;
    cloneAction: (action: ComposerOptionActionNode) => ComposerOptionActionNode;
    removeAction: (attributeID: string, optionID: string, actionID: string) => void;

    updateTemplate: (template: ComposerTemplateNode) => void;
    updateTemplateRestriction: (template: ComposerTemplateNode, sideID: number, key: keyof ComposerTemplateSideRestrictions, value: boolean) => void;
    cloneTemplate: (template: ComposerTemplateNode) => ComposerTemplateNode;
    removeTemplate: (templateID: string) => void;
    updateArea: (area: ComposerAreaNode) => void;
    cloneArea: (area: ComposerAreaNode) => ComposerAreaNode;
    removeArea: (areaID: string) => void;
    initializeTemplateInfo: (templates: ComposerTemplateNode[], areas: ComposerAreaNode[]) => void;

    setEcommerceSettings: (ecommerceSettings: ComposerEcommerceSettings) => void;
    selectAttributes: (attribute: ComposerAttributeNode[] | null) => void;
    selectOptions: (options: ComposerAttributeOptionNode[] | null) => void;
    selectActions: (actions: ComposerOptionActionNode[] | null) => void;
    selectTemplates: (templates: ComposerTemplateNode[] | null) => void;
    selectAreas: (areas: ComposerAreaNode[] | null) => void;

    setProductQuantity: (quantity: MPlaza.QuantityRule | null) => void;
    validateAttributes: () => void;
    validateOptions: (attribute: ComposerAttributeNode) => void;
};

// Create store with the modern create pattern
const useComposerStore = create<ComposerStore>((set, store) => {
    return {
        model: null,
        setModel: (model) => set({ model }),

        sceneMaterialList: [],
        sceneObjectList: [],
        sceneCameraList: [],
        setSceneMaterialList: (materials) => set({ sceneMaterialList: materials }),
        setSceneObjectList: (objects) => set({ sceneObjectList: objects }),
        setSceneCameraList: (cameras) => set({ sceneCameraList: cameras }),

        modelAttributes: null,
        attributes: [],
        attributesSteps: null,
        selectedAttributes: null,
        selectedOptions: null,
        selectedActions: null,
        attributesGroups: null,

        templates: [],
        selectedTemplates: null,
        areas: [],
        designs: new Map(),
        setDesigns: (designs: Map<string, MPlaza.Design>) => set({ designs }),
        selectedAreas: null,

        ecommerceSettings: null,
        fonts: null,
        setFonts: (fonts) => set({ fonts }),
        productQuantity: null,
        intializeAttributesInfo: (modelAttributes, attributes) => set({ modelAttributes, attributes }),
        setEcommerceSettings: (ecommerceSettings) => set({ ecommerceSettings }),
        selectAttributes: (attributes) => {
            // Reset selectedOptions only if the attribute selection actually changed
            set((state) => {
                const previousAttributeIds =
                    state.selectedAttributes
                        ?.map((attr) => attr.id)
                        .sort()
                        .join(',') || '';
                const newAttributeIds =
                    attributes
                        ?.map((attr) => attr.id)
                        .sort()
                        .join(',') || '';
                const attributeChanged = previousAttributeIds !== newAttributeIds;

                console.log('7️⃣ selectAttributes | changed:', attributeChanged, '| count:', attributes?.length);

                return {
                    selectedAttributes: attributes,
                    selectedOptions: attributeChanged ? [] : state.selectedOptions
                };
            });
        },
        selectOptions: (options) => {
            console.log('8️⃣ selectOptions | count:', options?.length || 0);
            set({ selectedOptions: options });
        },
        selectActions: (actions) => set({ selectedActions: actions }),
        selectTemplates: (templates) => set({ selectedTemplates: templates }),
        selectAreas: (areas) => set({ selectedAreas: areas }),
        //update if present, otherwise add new attribute
        updateAttribute: (attribute) => {
            set((state) => {
                const attr = state.attributes.find((a) => a.id === attribute.id);

                // Update selectedAttributes if this attribute is currently selected
                const updatedSelectedAttributes =
                    state.selectedAttributes?.map((selectedAttr) => (selectedAttr.id === attribute.id ? attribute : selectedAttr)) ?? null;

                if (attr) {
                    return {
                        attributes: state.attributes.map((a) => (a.id === attribute.id ? attribute : a)),
                        selectedAttributes: updatedSelectedAttributes,
                        selectedOptions: null
                    };
                }
                return {
                    attributes: [...state.attributes, attribute],
                    selectedAttributes: updatedSelectedAttributes,
                    selectedOptions: null
                };
            });
        },
        //append _clone to the id of cloned attributes
        cloneAttributes: (attributes) => {
            let clonedAttributes: ComposerAttributeNode[] = [];
            set((state) => {
                clonedAttributes = attributes.map((attr) => {
                    const clonedAttr = { ...attr, id: Zakeke.generateUUID(), idNumeric: -1, isNew: true };
                    clonedAttr.name = createSecureClonedName(
                        clonedAttr.name,
                        state.attributes.map((a) => a.name)
                    );
                    clonedAttr.code = createSecureClonedName(
                        clonedAttr.code,
                        state.attributes.map((a) => a.code)
                    );
                    clonedAttr.options = attr.options.map((option) => {
                        const clonedOption = { ...option, id: Zakeke.generateUUID(), idNumeric: -1, isNew: true };
                        clonedOption.name = createSecureClonedName(
                            clonedOption.name,
                            clonedAttr.options.map((opt) => opt.name)
                        );
                        clonedOption.code = createSecureClonedName(
                            clonedOption.code,
                            clonedAttr.options.map((opt) => opt.code)
                        );
                        clonedOption.actions = clonedOption.actions.map((action) => ({
                            ...action,
                            id: Zakeke.generateUUID(),
                            idNumeric: -1,
                            isNew: true
                        }));
                        return clonedOption;
                    });
                    return clonedAttr;
                });
                return { attributes: [...state.attributes, ...clonedAttributes] };
            });
            return clonedAttributes;
        },
        removeAttributes: (attributeIds) =>
            set((state) => {
                // Remove deleted attributes from selectedAttributes to prevent stale references
                const updatedSelectedAttributes = state.selectedAttributes?.filter((attr) => !attributeIds.includes(attr.id)) ?? null;

                return {
                    attributes: state.attributes.filter((attr) => !attributeIds.includes(attr.id)),
                    selectedAttributes: updatedSelectedAttributes?.length ? updatedSelectedAttributes : null,
                    selectedOptions: null
                };
            }),
        updateOption: (option) =>
            set((state) => {
                const attributeID = option.attributeID;
                const attributeIndex = state.attributes.findIndex((attr) => attr.id === attributeID);

                if (attributeIndex === -1) {
                    return state;
                }

                const attribute = state.attributes[attributeIndex];
                const optionInAttribute = attribute.options.find((opt) => opt.id === option.id);

                const updatedAttribute = {
                    ...attribute,
                    options: optionInAttribute ? attribute.options.map((opt) => (opt.id === option.id ? option : opt)) : [...attribute.options, option]
                };

                // Update selectedAttributes if this attribute is currently selected
                // This prevents the attribute from being deselected when options change
                const updatedSelectedAttributes =
                    state.selectedAttributes?.map((selectedAttr) => (selectedAttr.id === updatedAttribute.id ? updatedAttribute : selectedAttr)) ?? null;

                // Update selectedOptions if the updated option is currently selected
                const updatedSelectedOptions = state.selectedOptions?.map((selectedOpt) => (selectedOpt.id === option.id ? option : selectedOpt)) ?? null;

                return {
                    ...state,
                    attributes: [...state.attributes.slice(0, attributeIndex), updatedAttribute, ...state.attributes.slice(attributeIndex + 1)],
                    selectedAttributes: updatedSelectedAttributes,
                    selectedOptions: updatedSelectedOptions
                };
            }),
        cloneOption: (option) => {
            let clonedOption = null;
            set((state) => {
                const attributeID = option.attributeID;
                const attributeIndex = state.attributes.findIndex((attr) => attr.id === attributeID);
                const attribute = state.attributes[attributeIndex];
                if (attributeIndex > -1) {
                    clonedOption = { ...option, id: Zakeke.generateUUID(), idNumeric: -1, isNew: true };
                    clonedOption.name = createSecureClonedName(
                        clonedOption.name,
                        attribute.options.map((opt) => opt.name)
                    );
                    clonedOption.code = createSecureClonedName(
                        clonedOption.code,
                        attribute.options.map((opt) => opt.code)
                    );
                    clonedOption.actions = clonedOption.actions.map((action) => ({
                        ...action,
                        id: Zakeke.generateUUID(),
                        idNumeric: -1,
                        isNew: true
                    }));
                    const updatedOptions = [...state.attributes[attributeIndex].options, clonedOption];
                    const updatedAttribute = { ...state.attributes[attributeIndex], options: updatedOptions };

                    // Update selectedAttributes if this attribute is currently selected
                    const updatedSelectedAttributes =
                        state.selectedAttributes?.map((selectedAttr) => (selectedAttr.id === updatedAttribute.id ? updatedAttribute : selectedAttr)) ?? null;

                    return {
                        attributes: state.attributes.map((attr, index) => (index === attributeIndex ? updatedAttribute : attr)),
                        selectedAttributes: updatedSelectedAttributes
                    };
                }
                return state;
            });
            return clonedOption!;
        },
        removeOption: (attributeID, optionID) =>
            set((state) => {
                const attributeIndex = state.attributes.findIndex((attr) => attr.id === attributeID);
                if (attributeIndex === -1) {
                    return state;
                }

                const attribute = state.attributes[attributeIndex];

                const updatedAttribute = {
                    ...attribute,
                    options: attribute.options.filter((opt) => opt.id !== optionID)
                };

                return {
                    ...state,
                    attributes: [...state.attributes.slice(0, attributeIndex), updatedAttribute, ...state.attributes.slice(attributeIndex + 1)],
                    selectedOptions: state.selectedOptions?.filter((opt) => opt.id !== optionID) || null
                };
            }),
        //action childs of options
        updateAction: (action) =>
            set((state) => {
                const optionID = action.optionID;
                const attributeID = action.attributeID;

                const attributeIndex = state.attributes.findIndex((attr) => attr.id === attributeID);
                if (attributeIndex === -1) {
                    return state;
                }

                const attribute = state.attributes[attributeIndex];
                const optionIndex = attribute.options.findIndex((opt) => opt.id === optionID);
                if (optionIndex === -1) {
                    return state;
                }

                const option = attribute.options[optionIndex];
                const actionInOption = option.actions.find((a) => a.id === action.id);

                const updatedOption = {
                    ...option,
                    actions: actionInOption ? option.actions.map((a) => (a.id === action.id ? action : a)) : [...option.actions, action]
                };

                const updatedAttribute = {
                    ...attribute,
                    options: [...attribute.options.slice(0, optionIndex), updatedOption, ...attribute.options.slice(optionIndex + 1)]
                };

                // Update selectedAttributes if this attribute is currently selected
                const updatedSelectedAttributes =
                    state.selectedAttributes?.map((selectedAttr) => (selectedAttr.id === updatedAttribute.id ? updatedAttribute : selectedAttr)) ?? null;

                // Update selectedOptions if this option is currently selected
                const updatedSelectedOptions =
                    state.selectedOptions?.map((selectedOpt) => (selectedOpt.id === updatedOption.id ? updatedOption : selectedOpt)) ?? null;

                return {
                    ...state,
                    attributes: [...state.attributes.slice(0, attributeIndex), updatedAttribute, ...state.attributes.slice(attributeIndex + 1)],
                    selectedAttributes: updatedSelectedAttributes,
                    selectedOptions: updatedSelectedOptions
                };
            }),
        cloneAction: (action) => {
            let clonedAction = null;
            set((state) => {
                const attributeID = action.attributeID;
                const optionID = action.optionID;
                const attributeIndex = state.attributes.findIndex((attr) => attr.id === attributeID);
                const optionIndex = attributeIndex > -1 ? state.attributes[attributeIndex].options.findIndex((opt) => opt.id === optionID) : -1;

                if (attributeIndex > -1 && optionIndex > -1) {
                    clonedAction = { ...action, id: Zakeke.generateUUID(), idNumeric: -1, isNew: true };
                    const updatedActions = [...state.attributes[attributeIndex].options[optionIndex].actions, clonedAction];
                    const updatedOption = { ...state.attributes[attributeIndex].options[optionIndex], actions: updatedActions };
                    const updatedAttribute = {
                        ...state.attributes[attributeIndex],
                        options: state.attributes[attributeIndex].options.map((opt, index) => (index === optionIndex ? updatedOption : opt))
                    };

                    // Update selectedAttributes if this attribute is currently selected
                    const updatedSelectedAttributes =
                        state.selectedAttributes?.map((selectedAttr) => (selectedAttr.id === updatedAttribute.id ? updatedAttribute : selectedAttr)) ?? null;

                    // Update selectedOptions if this option is currently selected
                    const updatedSelectedOptions =
                        state.selectedOptions?.map((selectedOpt) => (selectedOpt.id === updatedOption.id ? updatedOption : selectedOpt)) ?? null;

                    return {
                        attributes: state.attributes.map((attr, index) => (index === attributeIndex ? updatedAttribute : attr)),
                        selectedAttributes: updatedSelectedAttributes,
                        selectedOptions: updatedSelectedOptions
                    };
                }
                return state;
            });
            return clonedAction!;
        },
        removeAction: (attributeID, optionID, actionID) =>
            set((state) => {
                const attributeIndex = state.attributes.findIndex((attr) => attr.id === attributeID);
                if (attributeIndex === -1) {
                    return state;
                }
                const attribute = state.attributes[attributeIndex];
                const optionIndex = attribute.options.findIndex((opt) => opt.id === optionID);
                if (optionIndex === -1) {
                    return state;
                }

                const option = attribute.options[optionIndex];

                const updatedOption = {
                    ...option,
                    actions: option.actions.filter((a) => a.id !== actionID)
                };

                const updatedAttribute = {
                    ...attribute,
                    options: [...attribute.options.slice(0, optionIndex), updatedOption, ...attribute.options.slice(optionIndex + 1)]
                };

                // Update selectedAttributes if this attribute is currently selected
                const updatedSelectedAttributes =
                    state.selectedAttributes?.map((selectedAttr) => (selectedAttr.id === updatedAttribute.id ? updatedAttribute : selectedAttr)) ?? null;

                // Update selectedOptions if this option is currently selected
                const updatedSelectedOptions =
                    state.selectedOptions?.map((selectedOpt) => (selectedOpt.id === updatedOption.id ? updatedOption : selectedOpt)) ?? null;

                return {
                    ...state,
                    attributes: [...state.attributes.slice(0, attributeIndex), updatedAttribute, ...state.attributes.slice(attributeIndex + 1)],
                    selectedAttributes: updatedSelectedAttributes,
                    selectedOptions: updatedSelectedOptions
                };
            }),

        updateTemplate: (template) =>
            set((state) => {
                const index = state.templates.findIndex((t) => t.id === template.id);

                // Update selectedTemplates if this template is currently selected
                const updatedSelectedTemplates =
                    state.selectedTemplates?.map((selectedTemplate) => (selectedTemplate.id === template.id ? template : selectedTemplate)) ?? null;

                if (index > -1) {
                    return {
                        templates: state.templates.map((t) => (t.id === template.id ? template : t)),
                        selectedTemplates: updatedSelectedTemplates
                    };
                }
                return {
                    templates: [...state.templates, template],
                    selectedTemplates: updatedSelectedTemplates
                };
            }),
        updateTemplateRestriction: (template, sideID, key, value) => {
            set((state) => {
                const index = state.templates.findIndex((t) => t.id === template.id);
                if (index === -1) {
                    return state;
                }
                const updatedRestrictions = new Map(template.restrictions);
                const sideRestrictions = updatedRestrictions.get(sideID) || {};
                sideRestrictions[key] = value;
                updatedRestrictions.set(sideID, sideRestrictions);
                const updatedTemplate = { ...template, restrictions: updatedRestrictions };

                // Update selectedTemplates if this template is currently selected
                const updatedSelectedTemplates =
                    state.selectedTemplates?.map((selectedTemplate) => (selectedTemplate.id === template.id ? updatedTemplate : selectedTemplate)) ?? null;
                return {
                    templates: state.templates.map((t) => (t.id === template.id ? updatedTemplate : t)),
                    selectedTemplates: updatedSelectedTemplates
                };
            });
        },
        getDesign: (templateID, areaID) => {
            const key = [templateID, areaID].join(',');
            return store().designs.get(key) || null;
        },
        updateDesign: (templateID, areaID, design) => {
            set((state) => {
                const key = [templateID, areaID].join(',');
                const updatedDesigns = new Map(state.designs);
                updatedDesigns.set(key, design);
                return {
                    ...state,
                    designs: updatedDesigns
                };
            });
        },
        cloneTemplate: (template) => {
            const clonedTemplate = { ...template, id: Zakeke.generateUUID() };
            set((state) => ({
                ...state,
                templates: [...state.templates, clonedTemplate]
                // No need to update selectedTemplates as cloned item is new
            }));
            return clonedTemplate;
        },
        removeTemplate: (templateID) =>
            set((state) => {
                // Remove deleted template from selectedTemplates to prevent stale references
                const updatedSelectedTemplates = state.selectedTemplates?.filter((t) => t.id !== templateID) ?? null;

                return {
                    ...state,
                    templates: state.templates.filter((t) => t.id !== templateID),
                    selectedTemplates: updatedSelectedTemplates?.length ? updatedSelectedTemplates : null
                };
            }),
        updateArea: (area) => {
            set((state) => {
                const index = state.areas.findIndex((a) => a.id === area.id);

                // Update selectedAreas if this area is currently selected
                const updatedSelectedAreas = state.selectedAreas?.map((selectedArea) => (selectedArea.id === area.id ? area : selectedArea)) ?? null;

                if (index > -1) {
                    return {
                        areas: state.areas.map((a) => (a.id === area.id ? area : a)),
                        selectedAreas: updatedSelectedAreas
                    };
                }
                const templates = state.templates;
                const designs = state.designs;
                if (!state.templates.length) {
                    const template = ComposerTemplateNodeAdapter.createNewTemplate('Default Template', state.model!, area.originalSide!);
                    templates.push(template);
                    designs.set([templates[0].templateID, area.sideID].join(','), template.design);
                }

                return {
                    areas: [...state.areas, area],
                    selectedAreas: updatedSelectedAreas,
                    templates,
                    designs
                };
            });
        },
        cloneArea: (area) => {
            const clonedArea = { ...area, id: Zakeke.generateUUID() };
            set((state) => ({
                ...state,
                areas: [...state.areas, clonedArea]
                // No need to update selectedAreas as cloned item is new
            }));
            return clonedArea;
        },
        removeArea: (areaID) =>
            set((state) => {
                // Remove deleted area from selectedAreas to prevent stale references
                const updatedSelectedAreas = state.selectedAreas?.filter((a) => a.id !== areaID) ?? null;

                return {
                    ...state,
                    areas: state.areas.filter((a) => a.id !== areaID),
                    selectedAreas: updatedSelectedAreas?.length ? updatedSelectedAreas : null
                };
            }),
        initializeTemplateInfo: (templates: ComposerTemplateNode[], areas: ComposerAreaNode[]) => {
            set((state) => {
                const designs = new Map();
                for (const template of templates) {
                    for (const area of areas) {
                        const key = [template.templateID, area.sideID].join(',');
                        designs.set(key, template.design);
                    }
                }
                return {
                    templates,
                    areas,
                    designs
                };
            });
        },
        validateAttributes: () => {
            LazyValidateAttributes(store().attributes).then((validatedAttributes) => {
                set((state) => {
                    // Update selectedAttributes to reflect validated attributes
                    const updatedSelectedAttributes =
                        state.selectedAttributes?.map((selectedAttr) => validatedAttributes.find((attr) => attr.id === selectedAttr.id) ?? selectedAttr) ??
                        null;

                    return {
                        attributes: validatedAttributes,
                        selectedAttributes: updatedSelectedAttributes
                    };
                });
            });
        },
        validateOptions: (attribute: ComposerAttributeNode) => {
            const currentAttribute = store().attributes.find((attr) => attr.id === attribute.id);
            if (!currentAttribute) return;

            LazyValidateOptions(currentAttribute, currentAttribute.options).then((validatedOptions) => {
                set((state) => {
                    const attributeIndex = state.attributes.findIndex((attr) => attr.id === attribute.id);
                    if (attributeIndex > -1) {
                        const updatedAttribute = { ...state.attributes[attributeIndex], options: validatedOptions };

                        // Update selectedAttributes if this attribute is currently selected
                        const updatedSelectedAttributes =
                            state.selectedAttributes?.map((selectedAttr) => (selectedAttr.id === updatedAttribute.id ? updatedAttribute : selectedAttr)) ??
                            null;

                        // Update selectedOptions to reflect the validated options
                        const updatedSelectedOptions =
                            state.selectedOptions?.map((selectedOpt) => validatedOptions.find((opt) => opt.id === selectedOpt.id) ?? selectedOpt) ?? null;

                        return {
                            attributes: state.attributes.map((attr, index) => (index === attributeIndex ? updatedAttribute : attr)),
                            selectedAttributes: updatedSelectedAttributes,
                            selectedOptions: updatedSelectedOptions
                        };
                    }
                    return state;
                });
            });
        },
        setProductQuantity: (quantity: MPlaza.QuantityRule | null) => {
            set((state) => ({
                ...state,
                productQuantity: quantity
            }));
        }
    };
});

// Selector helper (optional)
export const useComposerModelAttributes = () => useComposerStore((state) => state.modelAttributes);
export const useComposerAttributes = () => useComposerStore((state) => state.attributes);
export const useComposerAttributesSteps = () => useComposerStore((state) => state.attributesSteps);
export const useComposerAttributesGroups = () => useComposerStore((state) => state.attributesGroups);

export const useComposerEcommerceSettings = () => useComposerStore((state) => state.ecommerceSettings);

export const useSetComposerAttributes = () => useComposerStore((state) => state.intializeAttributesInfo);
export const useUpdateComposerAttributes = () => useComposerStore((state) => state.updateAttribute);
export const useSetComposerEcommerceSettings = () => useComposerStore((state) => state.setEcommerceSettings);

export const useSelectedComposerAttribute = () => useComposerStore((state) => state.selectedAttributes);
export const useSelectedComposerOptions = () => useComposerStore((state) => state.selectedOptions);
export const useSelectedComposerActions = () => useComposerStore((state) => state.selectedActions);

export const useComposerAttributesSelect = () => useComposerStore((state) => state.selectAttributes);
export const useComposerOptionsSelect = () => useComposerStore((state) => state.selectOptions);
export const useComposerActionsSelect = () => useComposerStore((state) => state.selectActions);

export default useComposerStore;
